/**
 * Breached-password rejection — our own implementation of the check that
 * Supabase gates behind the Pro plan (`auth_leaked_password_protection`).
 *
 * WHY WE OWN THIS: Supabase's built-in leaked-password protection is Pro-only
 * ($25/mo · $300/yr). The underlying data source — HaveIBeenPwned's Pwned
 * Passwords range API — is free, unauthenticated and unmetered, so we get the
 * same protection for $0 and keep it portable across surfaces. Decision logged
 * 2026-07-30; the Supabase advisory WARN stays open KNOWINGLY, not by oversight.
 *
 * HOW IT STAYS PRIVATE (k-anonymity): we never transmit the password, and never
 * transmit its full hash. We SHA-1 the password locally, send only the FIRST
 * FIVE hex characters of that hash, and receive every hash suffix sharing that
 * prefix (~800–2,000 of them). The comparison happens locally. HIBP therefore
 * cannot know which password — or even which full hash — was checked. This is
 * the same method Supabase's own implementation uses.
 *
 * SHA-1 is correct here and is NOT a security weakness: it is not protecting
 * anything, it is the index key HIBP's corpus is published under. Password
 * storage remains bcrypt inside Supabase Auth, untouched by this module.
 *
 * PLATFORM NOTE: hashing is injected rather than imported. `crypto.subtle`
 * exists in browsers but NOT in React Native, so callers supply a `Sha1Hex`
 * implementation — Web Crypto on web, `expo-crypto` on native. Same capability-
 * port pattern as `@auctisync/device`. This module stays platform-neutral and
 * dependency-free so web and both native apps share one implementation.
 */

/** HIBP's k-anonymity range endpoint. No API key, no account, no quota. */
export const HIBP_RANGE_ENDPOINT = 'https://api.pwnedpasswords.com/range';

/**
 * Breach count at or above which a password is rejected.
 *
 * 1 = reject on ANY appearance in the corpus, matching Supabase's behaviour.
 * A password that has leaked even once is in attackers' credential-stuffing
 * wordlists, which is the entire threat model. Named so it is tunable without
 * hunting through call sites.
 */
export const BREACH_COUNT_THRESHOLD = 1;

/**
 * Length of the hash prefix sent over the network. Five is HIBP's published
 * range size — NOT an arbitrary choice, and not ours to vary.
 */
export const HIBP_PREFIX_LENGTH = 5;

/**
 * Hashes a string to UPPERCASE hex SHA-1. Supplied by the caller so this
 * module needs no platform crypto of its own.
 */
export type Sha1Hex = (input: string) => Promise<string>;

/** Minimal `fetch` shape — keeps the module free of DOM/Node lib assumptions. */
export type FetchLike = (
  url: string,
  init?: { headers?: Record<string, string> },
) => Promise<{ ok: boolean; status: number; text: () => Promise<string> }>;

export interface BreachCheckResult {
  /** True only when the password was found at or above the threshold. */
  breached: boolean;
  /** How many times the password appears in the corpus. 0 when not found. */
  count: number;
  /**
   * Did the check actually complete?
   *
   * `false` means we FAILED OPEN — HIBP was unreachable, returned an error, or
   * the input was empty, so `breached: false` reflects "we could not tell",
   * not "we verified this password is safe". Callers that log should log this.
   */
  checked: boolean;
}

/** Fail-open result. Allowing the password is deliberate — see `checked`. */
function failOpen(): BreachCheckResult {
  return { breached: false, count: 0, checked: false };
}

/**
 * Is `password` present in HaveIBeenPwned's breach corpus?
 *
 * FAILS OPEN by design. If HIBP is down or the device is offline the password
 * is allowed and `checked` is `false`. Failing CLOSED would convert a
 * third-party outage into a total outage of both sign-up AND password
 * recovery — a self-inflicted denial of service strictly worse than the risk
 * this check mitigates. Supabase's own implementation fails open too.
 *
 * @param password  Plaintext. Never leaves this function.
 * @param sha1Hex   Platform SHA-1 (Web Crypto on web, expo-crypto on native).
 * @param fetchImpl Injectable for tests; defaults to global `fetch`.
 */
export async function checkPasswordBreached(
  password: string,
  sha1Hex: Sha1Hex,
  fetchImpl?: FetchLike,
): Promise<BreachCheckResult> {
  // Nothing to check, and an empty prefix would query a meaningless range.
  // Callers enforce minimum length separately; this is just a guard.
  if (!password) return failOpen();

  // No cast: `globalThis.fetch` is directly assignable to `FetchLike`. Kept
  // assertion-free deliberately so that if either signature ever drifts, the
  // compiler says so instead of a cast swallowing it.
  const doFetch: FetchLike | undefined = fetchImpl ?? globalThis.fetch;
  if (!doFetch) return failOpen();

  try {
    const hash = (await sha1Hex(password)).toUpperCase();
    // A malformed hash would silently turn into a wrong-range query, so treat
    // it as an inability to check rather than as a pass.
    if (hash.length <= HIBP_PREFIX_LENGTH) return failOpen();

    const prefix = hash.slice(0, HIBP_PREFIX_LENGTH);
    const suffix = hash.slice(HIBP_PREFIX_LENGTH);

    const response = await doFetch(`${HIBP_RANGE_ENDPOINT}/${prefix}`, {
      // Pads the response with decoy entries so an observer cannot infer the
      // queried prefix from response SIZE. Costs ~4% bandwidth; free privacy.
      headers: { 'Add-Padding': 'true' },
    });
    if (!response.ok) return failOpen();

    const count = findSuffixCount(await response.text(), suffix);
    return { breached: count >= BREACH_COUNT_THRESHOLD, count, checked: true };
  } catch {
    // Network failure, DNS failure, aborted request, crypto unavailable — all
    // mean "could not tell". Never block the user on our inability to check.
    return failOpen();
  }
}

/**
 * Finds `suffix` in an HIBP range response and returns its breach count.
 *
 * Response format is one `SUFFIX:COUNT` per line. Padding entries carry a
 * count of 0 — a matched padding entry therefore falls below the threshold and
 * correctly reads as "not breached", so padding can never cause a false
 * rejection. Exported for direct testing of the parser.
 */
export function findSuffixCount(body: string, suffix: string): number {
  const target = suffix.toUpperCase();
  for (const line of body.split('\n')) {
    const separator = line.indexOf(':');
    if (separator === -1) continue;
    if (line.slice(0, separator).trim().toUpperCase() !== target) continue;
    const count = Number.parseInt(line.slice(separator + 1).trim(), 10);
    return Number.isFinite(count) ? count : 0;
  }
  return 0;
}

/**
 * User-facing rejection copy, shared so web and native word it identically.
 *
 * Deliberately does NOT say "your password was leaked" — that is both
 * inaccurate (the STRING appears in a corpus; this account was not
 * necessarily involved) and needlessly alarming. Showing the count makes the
 * rejection read as legitimate rather than arbitrary.
 */
export function breachedPasswordMessage(count: number): string {
  const times = count > 0 ? ` (${count.toLocaleString('en-US')} times)` : '';
  return (
    `This password has appeared in a known data breach${times}. ` +
    `That doesn't mean your account was compromised — it means attackers ` +
    `already have this password in their lists. Please choose a different one.`
  );
}
